import {
  applySnapshot,
  getSnapshot,
  Instance,
  SnapshotIn,
  types,
} from 'mobx-state-tree';
import { performSetAction } from 'models/utils/utils';
import {
  BarGraphBox,
  BarGraphBoxProps,
} from 'models/reusable-parts/bar-graph-box';
import {
  SimpleTable,
  SimpleTableProps,
} from 'models/reusable-parts/simple-table/simple-table';
import {
  EventsFeed,
  EventsFeedProps,
} from 'models/report/events-feed/events-feed';
import { temporaryEndpoints } from 'http/endpoints';
import { AccountType } from 'globals/types';
import { BasicInfo, BasicInfoProps } from './basicInfo/basicInfo';
import {
  CashflowSummary,
  CashflowSummaryProps,
} from './cashflow-summary/cashflow-summary';
import {
  CashflowCharts,
  CashflowChartsProps,
} from './cashflow-charts/cashflow-charts';
import {
  RecurringTransactions,
  RecurringTransactionsProps,
} from './recurring-transactions/recurring-transactions';
import {
  FinancialCredibility,
  FinancialCredibilityProps,
} from './financial-credibility/financial-credibility';
import {
  BankAccountsList,
  BankAccountsListProps,
} from './bank-accounts-list/bank-accounts-list';
import {
  BankAccountsFinancialStats,
  BankAccountsFinancialStatsProps,
} from './bank-accounts-financial-stats/bank-accounts-financial-stats';
import {
  FinancialMarkers,
  FinancialMarkersProps,
} from './financial-markers/financial-markers';
import { Transactions, TransactionsProps } from './transactions/transactions';

export const AccountTypes: AccountType[] = Object.values(AccountType);

export interface ReportProps {
  userId: string | null;
  accountTypes: AccountType[];
  activeAccountType: AccountType;
  basicInfo: BasicInfoProps;
  cashflowCharts: CashflowChartsProps;
  cashflowSummary: CashflowSummaryProps;
  topRecurringIncomes: RecurringTransactionsProps;
  topRecurringExpenditures: RecurringTransactionsProps;
  financialCredibility: FinancialCredibilityProps;
  liquidity: BarGraphBoxProps;
  incomeStability: BarGraphBoxProps;
  rentStability: BarGraphBoxProps;
  financialMarkers: FinancialMarkersProps;
  events: SimpleTableProps;
  eventsFeed: EventsFeedProps;
  bankAccountsList: BankAccountsListProps;
  bankAccountsFinancialStats: BankAccountsFinancialStatsProps;
  recurringExpenditures: RecurringTransactionsProps;
  recurringIncomes: RecurringTransactionsProps;
  transactions: TransactionsProps;
}

export const Report = types
  .model({
    userId: types.maybeNull(types.string),
    accountTypes: types.optional(
      types.array(types.enumeration(AccountTypes)),
      []
    ),
    activeAccountType: types.optional(
      types.enumeration(AccountTypes),
      AccountType.PERSONAL
    ),
    basicInfo: types.optional(BasicInfo, {}),
    cashflowCharts: types.optional(CashflowCharts, {}),
    cashflowSummary: types.optional(CashflowSummary, {}),
    topRecurringIncomes: types.optional(RecurringTransactions, {}),
    topRecurringExpenditures: types.optional(RecurringTransactions, {}),
    financialCredibility: types.optional(FinancialCredibility, {}),
    liquidity: types.optional(BarGraphBox, {}),
    incomeStability: types.optional(BarGraphBox, {}),
    rentStability: types.optional(BarGraphBox, {}),
    financialMarkers: types.optional(FinancialMarkers, {}),
    events: types.optional(SimpleTable, {}),
    eventsFeed: types.optional(EventsFeed, {}),
    bankAccountsList: types.optional(BankAccountsList, {}),
    bankAccountsFinancialStats: types.optional(BankAccountsFinancialStats, {}),
    recurringExpenditures: types.optional(RecurringTransactions, {}),
    recurringIncomes: types.optional(RecurringTransactions, {}),
    transactions: types.optional(Transactions, {}),
  })
  .actions((self) => ({
    set(props: ReportProps): void {
      const currentSnapshot = getSnapshot(self);
      const newSnapshot = {
        ...currentSnapshot,
        ...props,
      };
      applySnapshot(self, newSnapshot);
    },
    async setBasicInfo(): Promise<void> {
      const { basicInfo, userId, activeAccountType } = self;
      await performSetAction({
        store: basicInfo,
        // endpoint: endpoints.basicInfo,
        endpoint: temporaryEndpoints.report.basicInfo,
        userId,
        accountType: activeAccountType,
      });
    },
    async setCashflowSummary(): Promise<void> {
      const { cashflowSummary, userId, activeAccountType } = self;
      await performSetAction({
        store: cashflowSummary,
        // endpoint: endpoints.cashflowSummary,
        endpoint: temporaryEndpoints.report.cashflowSummary,
        userId,
        accountType: activeAccountType,
      });
    },
    async setCashflowCharts(): Promise<void> {
      const { cashflowCharts, userId, activeAccountType } = self;
      await performSetAction({
        store: cashflowCharts,
        // endpoint: endpoints.cashflowCharts,
        endpoint: temporaryEndpoints.report.cashflowCharts,
        userId,
        accountType: activeAccountType,
      });
    },
    async setTopRecurringExpenditures(): Promise<void> {
      const { topRecurringExpenditures, userId, activeAccountType } = self;
      await performSetAction({
        store: topRecurringExpenditures,
        // endpoint: endpoints.topRecurringExpenditures,
        endpoint: temporaryEndpoints.report.topRecurringExpenditures,
        userId,
        accountType: activeAccountType,
      });
    },
    async setTopRecurringIncomes(): Promise<void> {
      const { topRecurringIncomes, userId, activeAccountType } = self;
      await performSetAction({
        store: topRecurringIncomes,
        // endpoint: endpoints.topRecurringIncomes,
        endpoint: temporaryEndpoints.report.topRecurringIncomes,
        userId,
        accountType: activeAccountType,
      });
    },
    async setFinancialCredibility(): Promise<void> {
      const { financialCredibility, userId, activeAccountType } = self;
      await performSetAction({
        store: financialCredibility,
        // endpoint: endpoints.financialCredibility,
        endpoint: temporaryEndpoints.report.financialCredibility,
        userId,
        accountType: activeAccountType,
      });
    },
    async setLiquidity(): Promise<void> {
      const { liquidity, userId, activeAccountType } = self;
      await performSetAction({
        store: liquidity,
        // endpoint: endpoints.liquidity,
        endpoint: temporaryEndpoints.report.liquidity,
        userId,
        accountType: activeAccountType,
      });
    },
    async setIncomeStability(): Promise<void> {
      const { incomeStability, userId, activeAccountType } = self;
      await performSetAction({
        store: incomeStability,
        // endpoint: endpoints.incomeStability,
        endpoint: temporaryEndpoints.report.incomeStability,
        userId,
        accountType: activeAccountType,
      });
    },
    async setRentStability(): Promise<void> {
      const { rentStability, userId, activeAccountType } = self;
      await performSetAction({
        store: rentStability,
        // endpoint: endpoints.rentStability,
        endpoint: temporaryEndpoints.report.rentStability,
        userId,
        accountType: activeAccountType,
      });
    },
    async setFinancialMarkers(): Promise<void> {
      const { financialMarkers, userId, activeAccountType } = self;
      await performSetAction({
        store: financialMarkers,
        // endpoint: endpoints.financialMarkers,
        endpoint: temporaryEndpoints.report.financialMarkers,
        userId,
        accountType: activeAccountType,
      });
    },
    async setEvents(): Promise<void> {
      const { events, userId, activeAccountType } = self;
      await performSetAction({
        store: events,
        // endpoint: endpoints.events,
        endpoint: temporaryEndpoints.report.events,
        userId,
        accountType: activeAccountType,
      });
    },
    async setEventsFeed(): Promise<void> {
      const { eventsFeed, userId, activeAccountType } = self;
      await performSetAction({
        store: eventsFeed,
        // endpoint: endpoints.eventsFeed,
        endpoint: temporaryEndpoints.report.eventsFeed,
        userId,
        accountType: activeAccountType,
      });
    },
    async setBankAccountsList(): Promise<void> {
      const { bankAccountsList, userId, activeAccountType } = self;
      await performSetAction({
        store: bankAccountsList,
        // endpoint: endpoints.bankAccountsList,
        endpoint: temporaryEndpoints.report.bankAccountsList,
        userId,
        accountType: activeAccountType,
      });
    },
    async setBankAccountsFinancialStats(): Promise<void> {
      const { bankAccountsFinancialStats, userId, activeAccountType } = self;
      await performSetAction({
        store: bankAccountsFinancialStats,
        // endpoint: endpoints.bankAccountsFinancialStats,
        endpoint: temporaryEndpoints.report.bankAccountsFinancialStats,
        userId,
        accountType: activeAccountType,
      });
    },
    async setRecurringExpenditures(): Promise<void> {
      const { recurringExpenditures, userId, activeAccountType } = self;
      await performSetAction({
        store: recurringExpenditures,
        // endpoint: endpoints.recurringExpenditures,
        endpoint: temporaryEndpoints.report.recurringExpenditures,
        userId,
        accountType: activeAccountType,
      });
    },
    async setRecurringIncomes(): Promise<void> {
      const { recurringIncomes, userId, activeAccountType } = self;
      await performSetAction({
        store: recurringIncomes,
        // endpoint: endpoints.recurringIncomes,
        endpoint: temporaryEndpoints.report.recurringIncomes,
        userId,
        accountType: activeAccountType,
      });
    },
    async setTransactions(): Promise<void> {
      const { transactions, userId, activeAccountType } = self;
      await performSetAction({
        store: transactions,
        // endpoint: endpoints.recurringIncomes,
        endpoint: temporaryEndpoints.report.transactions,
        userId,
        accountType: activeAccountType,
      });
    },
    async setUserId(userId: string | null): Promise<void> {
      const currentSnapshot = getSnapshot(self);
      const newSnapshot = {
        ...currentSnapshot,
        userId,
      };
      applySnapshot(self, newSnapshot);
    },
    async setAccountTypes(accountTypes: AccountType[]): Promise<void> {
      const currentSnapshot = getSnapshot(self);
      const newSnapshot = {
        ...currentSnapshot,
        accountTypes,
      };
      applySnapshot(self, newSnapshot);
    },
  }))
  .actions((self) => ({
    update(): void {
      self.setCashflowSummary();
      self.setCashflowCharts();
      self.setTopRecurringIncomes();
      self.setTopRecurringExpenditures();
      self.setFinancialCredibility();
      self.setLiquidity();
      self.setIncomeStability();
      self.setRentStability();
      self.setFinancialMarkers();
      self.setEvents();
      self.setEventsFeed();
      self.setBankAccountsList();
      self.setBankAccountsFinancialStats();
      self.setTransactions();
      self.setRecurringExpenditures();
      self.setRecurringIncomes();
    },
  }))
  .actions((self) => ({
    init(): void {
      self.setBasicInfo();
      self.update();
    },
  }))
  .actions((self) => ({
    afterCreate(): void {
      if (!self.userId || !self.accountTypes.length) return;
      self.init();
    },
    updateAfterAccountTypeChange(): void {
      if (!self.userId || !self.accountTypes.length) return;
      self.update();
    },
  }))
  .actions((self) => ({
    async setActiveAccountType(accountTypes: AccountType): Promise<void> {
      const currentSnapshot = getSnapshot(self);
      const newSnapshot = {
        ...currentSnapshot,
        activeAccountType: accountTypes,
      };
      applySnapshot(self, newSnapshot);
      self.updateAfterAccountTypeChange();
    },
  }));

export type ReportSnapshot = SnapshotIn<typeof Report>;
export type ReportInstance = Instance<typeof Report>;
